/**
 * vLLM: one process per model, and this console is what starts them.
 *
 * Deliberately its own page rather than more rows in the Models table, because
 * almost nothing transfers. An Ollama row is a file on disk that may or may not
 * be loaded. A vLLM row is a running program with a port, a pid, a command line
 * and a log — and when it goes wrong, the only useful thing on screen is the
 * reason it died. Sharing a table would mean six columns that are blank for one
 * provider and six that are blank for the other.
 *
 * Three things here are deliberate:
 *
 *   A stopped model stays on the list. One that died at 3am should still be
 *   there in the morning with its reason attached. Dropping it is how a crash
 *   becomes invisible.
 *
 *   Starting is slow and says so. A first start downloads weights and compiles,
 *   which is minutes. The progress line is lifted straight from vLLM's own log,
 *   because twenty silent minutes is indistinguishable from a hang.
 *
 *   The exact command is on screen. "Why is this model behaving differently
 *   from the one I started yesterday" is otherwise unanswerable.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useLive } from "./live";
import { SettingsFields, chosen, defaults } from "./VllmSettings";
import {
  ApiError,
  fetchJobs,
  fetchNodes,
  fetchStatus,
  fetchVllmSchema,
  fetchVllmLogs,
  fetchVllmModels,
  forgetVllmModel,
  reconfigureVllmModel,
  startVllmModel,
  stopVllmModel,
  type Job,
  type VllmModel,
  type VllmSettings,
} from "./api";

export default function VllmPage() {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const [logsFor, setLogsFor] =
    useState<{ name: string; node: string | null; live: boolean } | null>(null);
  const [settingsFor, setSettingsFor] = useState<VllmModel | null>(null);

  const status = useQuery({ queryKey: ["status"], queryFn: fetchStatus });
  const models = useQuery({
    queryKey: ["vllm", "models"],
    queryFn: fetchVllmModels,
    // A router merges what its nodes report; a node answers for itself. Either
    // way this is the one list, so a failure here is a real failure and worth
    // showing rather than retrying into silence.
    retry: false,
    // A vLLM process can die on its own, which is the same kind of fact as a
    // model dropping out of memory — so it shares that setting. Off by default;
    // starting and stopping from here refreshes it either way.
    refetchInterval: useLive("live_running"),
  });
  const jobs = useQuery({
    queryKey: ["jobs"],
    queryFn: fetchJobs,
    // Only while something is happening. A start takes minutes and its progress
    // line is the only sign of life, so this one earns its place — but it stops
    // the moment nothing is running.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((j) => j.state === "running") ? 2000 : false,
  });
  // A single-machine install answers 400 here, which is the honest signal that
  // there is nothing to choose between. Failure is expected, so it is not
  // shown — the picker simply does not appear.
  const nodes = useQuery({
    queryKey: ["nodes"],
    queryFn: fetchNodes,
    retry: false,
  });
  const nodeNames = (nodes.data ?? []).filter((n) => n.enabled).map((n) => n.name);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["vllm"] });
    void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    void queryClient.invalidateQueries({ queryKey: ["connections"] });
  };

  const runtime = status.data?.vllm;
  // Only once there is more than one machine. On a single install the column
  // would be one repeated value, which is noise where a table wants signal.
  const multiNode = (models.data ?? []).some((m) => m.node);
  // Jobs are keyed by the SERVED name, which is what /vllm/start_model returns.
  const busy = new Map(
    (jobs.data ?? [])
      .filter((j) => j.provider === "vllm" && j.state === "running")
      .map((j) => [j.model, j]),
  );

  return (
    <main>
      <div className="page-head">
        <div>
          <h1>vLLM</h1>
          <p>One server per model. Started and stopped from here.</p>
        </div>
        <div className="acts">
          <button
            className="btn"
            type="button"
            onClick={() => void models.refetch()}
            disabled={models.isFetching}
          >
            {models.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {problem && (
        <div className="notice">
          <h3>That did not work</h3>
          <p>{problem}</p>
        </div>
      )}

      {models.error ? (
        <div className="notice">
          <h3>Cannot list models</h3>
          <p>
            {models.error instanceof ApiError
              ? models.error.detail
              : String(models.error)}
          </p>
        </div>
      ) : (
        <>
          {/* A warning, never a gate. This used to replace the page, which meant
              a wrong answer here hid the form for starting the first model —
              and the answer WAS wrong: "has vLLM" was being inferred from "has
              vLLM models", so a machine with vLLM and nothing running reported
              not installed. The page now always offers the way forward. */}
          {runtime && !runtime.reachable && (
            <div className="notice warn">
              <h3>No machine here has vLLM</h3>
              <p>{runtime.error}</p>
              <p style={{ marginTop: 6 }}>
                On the machine that should run it, point{" "}
                <code>MODELS_VLLM_BIN</code> at the <code>vllm</code> command —
                it usually lives in a different virtual environment from this
                server.
              </p>
            </div>
          )}

          <div className="card">
            <header>
              <h2>Models</h2>
              <div className="r">
                <span className="hint">
                  {runtime?.version ? `vLLM ${runtime.version}` : ""}
                </span>
              </div>
            </header>

            {models.isLoading ? (
              <div className="empty">Loading…</div>
            ) : models.data && models.data.length > 0 ? (
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Model</th>
                      {multiNode && <th>Node</th>}
                      <th>Type</th>
                      <th>State</th>
                      <th className="right">Context</th>
                      <th className="right">Port</th>
                      <th className="right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.data.map((m) => (
                      <Row
                        key={m.node ? `${m.node}/${m.name}` : m.name}
                        model={m}
                        multiNode={multiNode}
                        job={busy.get(m.name)}
                        onChanged={refresh}
                        onProblem={setProblem}
                        onSettings={() => setSettingsFor(m)}
                        onLogs={() =>
                          setLogsFor({
                            name: m.name,
                            node: m.node,
                            live: m.state === "starting" || m.state === "ready",
                          })
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty">
                Nothing started yet. Add one below — the first start downloads
                the weights, so give it a few minutes.
              </div>
            )}

            <div className="card-foot">
              <StartBox
                onStarted={refresh}
                onProblem={setProblem}
                nodes={nodeNames}
              />
            </div>
          </div>

          <div className="info">
            A vLLM model keeps running when this server restarts, and is picked
            up again on the way back. Nothing here is uninstalled by Stop — the
            weights stay in the Hugging Face cache and a restart is quick.
          </div>
        </>
      )}

      {settingsFor && (
        <SettingsDialog
          model={settingsFor}
          onDone={refresh}
          onProblem={setProblem}
          onClose={() => setSettingsFor(null)}
        />
      )}

      {logsFor && (
        <LogDialog
          name={logsFor.name}
          node={logsFor.node}
          live={logsFor.live}
          onClose={() => setLogsFor(null)}
        />
      )}
    </main>
  );
}

function Row({
  model,
  multiNode,
  job,
  onChanged,
  onProblem,
  onSettings,
  onLogs,
}: {
  model: VllmModel;
  multiNode: boolean;
  job: Job | undefined;
  onChanged: () => void;
  onProblem: (message: string) => void;
  onSettings: () => void;
  onLogs: () => void;
}) {
  const [showArgs, setShowArgs] = useState(false);

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onMutate: () => onProblem(""),
    onError: (e) => onProblem(e instanceof ApiError ? e.detail : String(e)),
    onSuccess: onChanged,
  });

  const live = model.state === "starting" || model.state === "ready";

  return (
    <tr>
      <td>
        <div className="mname">
          <b>{model.name}</b>
          {/* The served name is often an alias, so the real repo is worth
              showing — otherwise nobody can tell two aliases apart. */}
          <span>{model.model}</span>
        </div>
        {model.error && <p className="problem">{model.error}</p>}
        {job && <p className="hint">{job.detail ?? "working"}</p>}
        {showArgs && <pre className="argline">{model.args.join(" ")}</pre>}
      </td>
      {multiNode && (
        <td>
          <span className="chip node">{model.node ?? "—"}</span>
        </td>
      )}
      <td>
        <span className="chip">{model.task === "embed" ? "embedding" : "chat"}</span>
      </td>
      <td>
        {job ? (
          <span className="pill busy"><i />{job.kind === "load" ? "Starting" : "Stopping"}</span>
        ) : model.state === "ready" ? (
          <span className="pill loaded"><i />Running</span>
        ) : model.state === "starting" ? (
          <span className="pill busy"><i />Starting</span>
        ) : model.state === "failed" ? (
          <span className="pill fail"><i />Failed</span>
        ) : (
          <span className="pill idle"><i />Stopped</span>
        )}
      </td>
      {/* Read back from vLLM, never echoed from the settings. This is the
          number the model is actually serving, which is the only version of it
          worth putting on screen. */}
      <td className="right num">
        {model.context_length != null
          ? model.context_length.toLocaleString()
          : "—"}
      </td>
      <td className="right num">{live ? model.port : "—"}</td>
      <td className="right">
        <div className="rowacts">
          <button className="btn sm" type="button" onClick={() => setShowArgs(!showArgs)}>
            {showArgs ? "Hide" : "Command"}
          </button>
          <button
            className="btn sm"
            type="button"
            disabled={!!job}
            onClick={onSettings}
          >
            Settings
          </button>
          <button className="btn sm" type="button" onClick={onLogs}>
            Log
          </button>
          {live ? (
            <button
              className="btn sm"
              type="button"
              disabled={!!job || act.isPending}
              onClick={() => act.mutate(() => stopVllmModel(model.name, model.node))}
            >
              Stop
            </button>
          ) : (
            <>
              <button
                className="btn sm primary"
                type="button"
                disabled={!!job || act.isPending}
                onClick={() =>
                  act.mutate(() =>
                    startVllmModel({
                      model: model.model,
                      name: model.name,
                      task: model.task,
                      // What the operator typed last time, which the server
                      // stored separately for exactly this. Re-deriving it from
                      // the full command line does not work — the server's own
                      // MODELS_VLLM_EXTRA_ARGS is in there and gets appended
                      // again, and vLLM dies on the duplicate.
                      settings: model.settings,
                      extra_args: model.extra,
                      node: model.node,
                    }),
                  )
                }
              >
                Start
              </button>
              <button
                className="btn sm danger"
                type="button"
                disabled={!!job || act.isPending}
                onClick={() => {
                  if (!confirm(`Remove ${model.name} from this list?\n\nIts weights stay in the cache. Nothing is deleted.`)) return;
                  act.mutate(() => forgetVllmModel(model.name, model.node));
                }}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function StartBox({
  onStarted,
  onProblem,
  nodes,
}: {
  onStarted: () => void;
  onProblem: (message: string) => void;
  /** The machines available to start on. Empty on a single-machine setup,
   *  where there is nothing to choose and the picker does not appear. */
  nodes: string[];
}) {
  const [model, setModel] = useState("");
  const [name, setName] = useState("");
  const [task, setTask] = useState<"auto" | "generate" | "embed">("auto");
  const [extra, setExtra] = useState("");
  const [node, setNode] = useState("");
  const [settings, setSettings] = useState<VllmSettings>({});
  const [showSettings, setShowSettings] = useState(false);

  // The form for THIS model on THIS machine, asked for once a name is typed.
  // Nothing to draw before then: presets, precision and the pre-ticked
  // remote-code switch all come from the model's own config, and a generic
  // form let people pick 32k for a model whose ceiling is 512.
  //
  // Keyed on model and machine, kept forever: a config on Hugging Face does not
  // change while the page is open. A router has to know the machine too, since
  // "split across GPUs" depends on what that box has.
  const wanted = model.trim();
  const schema = useQuery({
    queryKey: ["vllm", "schema", wanted, node || null],
    queryFn: () => fetchVllmSchema(wanted, node || null),
    enabled: wanted.length > 0 && (nodes.length === 0 || !!node),
    staleTime: Infinity,
    retry: false,
  });

  // When a form arrives for a new model, start from ITS defaults — which for a
  // model that needs its own code means that switch already on. Anything the
  // operator typed before the form loaded is kept over the defaults.
  useEffect(() => {
    if (schema.data) setSettings((v) => ({ ...defaults(schema.data), ...v }));
  }, [schema.data]);

  const start = useMutation({
    mutationFn: () =>
      startVllmModel({
        model: model.trim(),
        name: name.trim() || null,
        task,
        settings: chosen(settings),
        // Split on whitespace, which is what a person typing flags expects.
        extra_args: extra.trim() ? extra.trim().split(/\s+/) : [],
        node: node || null,
      }),
    onMutate: () => onProblem(""),
    onError: (e) => onProblem(e instanceof ApiError ? e.detail : String(e)),
    onSuccess: () => {
      setModel("");
      setName("");
      setExtra("");
      setSettings({});
      setShowSettings(false);
      onStarted();
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!model.trim() || start.isPending) return;
    start.mutate();
  }

  return (
    <form className="addnode" onSubmit={onSubmit}>
      <h3>Start a model</h3>
      <div className="addgrid">
        <label>
          Hugging Face model
          <input
            value={model}
            placeholder="BAAI/bge-small-en-v1.5"
            onChange={(e) => setModel(e.target.value)}
          />
        </label>
        <label>
          Call it <span className="unit">optional</span>
          <input
            value={name}
            placeholder="bge"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Type
          <select
            value={task}
            onChange={(e) =>
              setTask(e.target.value as "auto" | "generate" | "embed")
            }
          >
            <option value="auto">Work it out</option>
            <option value="generate">Chat / completion</option>
            <option value="embed">Force embedding</option>
          </select>
        </label>
        <label>
          Extra flags <span className="unit">optional</span>
          <input
            value={extra}
            placeholder="--max-model-len 4096 --dtype half"
            onChange={(e) => setExtra(e.target.value)}
          />
        </label>
        {/* Only when there is a choice. A router refuses a start that names no
            machine — deliberately, since where a model runs is a decision about
            hardware and only the operator knows which box they meant. */}
        {nodes.length > 0 && (
          <label>
            Machine
            <select value={node} onChange={(e) => setNode(e.target.value)}>
              <option value="">Which machine…</option>
              {nodes.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* A link rather than an open panel. Most starts need none of this, and
          nine fields in front of somebody who wants to type a name and press
          Start is a worse first impression.

          Nothing at all until a model is named: the form is drawn for that
          model, and there is no honest generic one. A model Hugging Face cannot
          describe gets its reason shown here instead of a form — the free-text
          flags still work for it. */}
      {wanted && nodes.length > 0 && !node && (
        <p className="hint">Pick a machine to see this model&rsquo;s settings.</p>
      )}
      {schema.isFetching && !schema.data && (
        <p className="hint">Reading {wanted} on Hugging Face…</p>
      )}
      {schema.error && (
        <p className="problem">
          No settings for this model:{" "}
          {schema.error instanceof ApiError
            ? schema.error.detail
            : String(schema.error)}{" "}
          Extra flags still apply.
        </p>
      )}
      {schema.data && (
        <>
          <button
            className="linkish"
            type="button"
            onClick={() => setShowSettings(!showSettings)}
          >
            {showSettings ? "Hide settings" : "Settings"}
            {!showSettings && Object.keys(chosen(settings)).length > 0 && (
              <span className="cnt">{Object.keys(chosen(settings)).length}</span>
            )}
          </button>
          {showSettings && (
            <SettingsFields
              schema={schema.data}
              values={settings}
              onChange={setSettings}
            />
          )}
        </>
      )}

      <div className="addfoot">
        <button
          className="btn primary"
          type="submit"
          disabled={
            !model.trim() || start.isPending || (nodes.length > 0 && !node)
          }
        >
          {start.isPending ? "Starting…" : "Start"}
        </button>
        <span className="hint">
          {/* Leave the type alone unless you mean it. vLLM reads the model's own
              config and picks correctly; the override exists only to press a
              chat model into embedding duty, which nothing can infer. */}
          Leave the type on <b>Work it out</b> — vLLM reads the model and
          decides. A first start downloads the weights, so allow several
          minutes.
        </span>
      </div>
    </form>
  );
}

/**
 * Settings for a model that already exists.
 *
 * Saving restarts it, and the button says so. A vLLM setting is a launch
 * argument — the process reads it once and cannot be told otherwise — so a
 * "Save" that quietly meant "next time you restart" would be a lie the console
 * tells once and the operator discovers much later.
 */
function SettingsDialog({
  model,
  onDone,
  onProblem,
  onClose,
}: {
  model: VllmModel;
  onDone: () => void;
  onProblem: (message: string) => void;
  onClose: () => void;
}) {
  // Opens on what it was started with, not on defaults.
  const [values, setValues] = useState<VllmSettings>(model.settings ?? {});
  // Drawn for this model on the machine it runs on — same form the start box
  // showed, so a setting means the same thing in both places.
  const schema = useQuery({
    queryKey: ["vllm", "schema", model.model, model.node],
    queryFn: () => fetchVllmSchema(model.model, model.node),
    staleTime: Infinity,
    retry: false,
  });

  // The model's own defaults underneath what was chosen, so a model that needs
  // its own code keeps that switch on even if it was started before the form
  // existed and has no record of it.
  useEffect(() => {
    if (schema.data) setValues((v) => ({ ...defaults(schema.data), ...v }));
  }, [schema.data]);

  const save = useMutation({
    mutationFn: () =>
      reconfigureVllmModel(model.name, chosen(values), model.node),
    onError: (e) => onProblem(e instanceof ApiError ? e.detail : String(e)),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const live = model.state === "starting" || model.state === "ready";

  return (
    <div
      className="scrim"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dlg wide" role="dialog" aria-modal="true"
           aria-label={`Settings for ${model.name}`}>
        <header>
          <div>
            <h2>{model.name}</h2>
            <p className="hint">
              {model.model}
              {model.node ? ` on ${model.node}` : ""}
            </p>
          </div>
          <button className="btn sm" type="button" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="dlg-body">
          {schema.error ? (
            <p className="problem">
              {schema.error instanceof ApiError
                ? schema.error.detail
                : String(schema.error)}
            </p>
          ) : schema.data ? (
            <SettingsFields
              schema={schema.data}
              values={values}
              onChange={setValues}
            />
          ) : (
            <p className="hint">Loading…</p>
          )}
        </div>

        <footer>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="button"
            disabled={save.isPending || !schema.data}
            onClick={() => save.mutate()}
          >
            {save.isPending
              ? "Applying…"
              : live
                ? "Restart with these"
                : "Save and start"}
          </button>
          <span className="hint">
            {live
              ? "This stops the model and starts it again. It is unreachable for a minute or two."
              : "This starts the model with these settings."}
          </span>
        </footer>
      </div>
    </div>
  );
}

function LogDialog({
  name,
  node,
  live,
  onClose,
}: {
  name: string;
  node: string | null;
  /** Whether the model is still starting or running. A stopped model's log is
   *  finished and cannot grow, so polling it is asking a question with a fixed
   *  answer — which is the same mistake the rest of this console just stopped
   *  making. */
  live: boolean;
  onClose: () => void;
}) {
  const logs = useQuery({
    queryKey: ["vllm", "logs", node, name],
    queryFn: () => fetchVllmLogs(name, 400, node),
    refetchInterval: live ? 3_000 : false,
  });

  return (
    <div
      className="scrim"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dlg wide" role="dialog" aria-modal="true" aria-label={`${name} log`}>
        <header>
          <div>
            <h2>{name}</h2>
            <p className="hint">
              vLLM&rsquo;s own output{node ? ` on ${node}` : ""}.{" "}
              {live ? "Refreshes while this is open." : "This model has stopped, so the log is final."}
            </p>
          </div>
          <button className="btn sm" type="button" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="dlg-body">
          {logs.error ? (
            <p className="problem">
              {logs.error instanceof ApiError ? logs.error.detail : String(logs.error)}
            </p>
          ) : (
            <pre className="logbox">
              {(logs.data?.lines ?? []).join("\n") || "Nothing logged yet."}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
